import { Navigate } from 'react-router-dom';
import { useAuthStore, UserType } from '../../stores/auth.store';

interface ProfileRouteProps {
  allowedTypes: UserType[];
  children: React.ReactNode;
}

/**
 * Guard que valida se o usuário tem o tipo correto para acessar a rota.
 * Redireciona para a rota correta do perfil se não autorizado.
 * 
 * Super admin pode acessar qualquer rota.
 * 
 * @example
 * <ProfileRoute allowedTypes={['CUSTOMER']}>
 *   <CustomerDashboard />
 * </ProfileRoute>
 */
export function ProfileRoute({ allowedTypes, children }: ProfileRouteProps): JSX.Element {
  const { user, isCorrentista, isSuperAdmin } = useAuthStore();

  // Super admin pode acessar qualquer rota
  if (isSuperAdmin()) {
    return <>{children}</>;
  }

  // Verifica se é correntista
  if (!isCorrentista()) {
    return <Navigate to="/" replace />;
  }

  // Verifica se o tipo é permitido
  if (!allowedTypes.includes(user?.type as UserType)) {
    const correctRoute = getProfileRoute(user?.type);
    return <Navigate to={correctRoute} replace />;
  }

  return <>{children}</>;
}

/**
 * Retorna a rota base do perfil baseado no tipo do usuário.
 */
function getProfileRoute(userType: UserType | undefined): string {
  switch (userType) {
    case 'CUSTOMER':
      return '/banking/customer';
    case 'SUPPLIER':
      return '/banking/supplier';
    case 'EMPLOYEE':
      return '/banking/employee';
    default:
      return '/banking/customer';
  }
}

export default ProfileRoute;
